import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { athleteSportsOf, fetchAPI, formatSportLabel } from "../lib/utils";
import { PerformanceProfilePreview } from "./PerformanceProfilePreview";
import { WeightClassComparison } from "./performance/WeightClassComparison";
import { SportTabs } from "./sports/SportTabs";
import { AthleteModelBar } from "./sportConfig/AthleteModelBar";

/**
 * Il profilo prestativo di un atleta visto dal coach, **un tab per sport**.
 *
 * Lista atleti e scheda atleta chiamavano il profilo senza `?sport=`: il coach
 * vedeva solo la primaria, e il profilo IPSC di un atleta CrossFit + IPSC per
 * lui non esisteva (B4 di docs/sport-config/ANALYSIS.md). Qui stanno, una
 * volta sola, la scelta del tab e il caricamento per sport; la resa resta in
 * `PerformanceProfilePreview`, che non va duplicato.
 *
 * Lo scope sono gli sport **dell'atleta**, mai gli ambiti del coach che
 * guarda (decisione D13): un coach di solo tiro vede anche la parte fisica.
 *
 * Nei modelli per categoria di peso il coach puo' leggere l'atleta anche sulla
 * tabella di un'altra categoria (D6b di docs/sport-config): la scelta sta nella
 * barra del modello, il confronto si carica qui e si mostra sopra il profilo.
 *
 * Props:
 *  - athlete: il documento dell'atleta (servono `user_id` e `sports`/`sport`)
 *  - sportsOptions: [{ value, label }] se il chiamante li ha gia'; altrimenti
 *    si caricano, e solo quando servono le etichette dei tab.
 */
export function CoachAthletePerformanceProfile({ athlete, sportsOptions = null }) {
	const { t } = useLanguage();
	const athleteId = athlete?.user_id || null;
	const sportsKey = athleteSportsOf(athlete).join("|");
	const sports = useMemo(
		() => (sportsKey ? sportsKey.split("|") : []),
		[sportsKey],
	);
	const multi = sports.length > 1;

	const [activeSport, setActiveSport] = useState(null);
	const currentSport =
		activeSport && sports.includes(activeSport) ? activeSport : sports[0] || null;

	const [domains, setDomains] = useState([]);
	const [loading, setLoading] = useState(Boolean(athleteId));
	const [loadedLabels, setLoadedLabels] = useState([]);
	// Il confronto fra categorie: `{key, label, baseLabel}` e i domini letti
	// sulla tabella di quella categoria.
	const [compare, setCompare] = useState(null);
	const [compareDomains, setCompareDomains] = useState(null);
	const requestSeq = useRef(0);
	// Il messaggio d'errore non deve far ripartire il caricamento: `t` sta in
	// un ref, fuori dalle dipendenze di `load`.
	const tRef = useRef(t);
	useEffect(() => {
		tRef.current = t;
	}, [t]);

	// Un atleta nuovo riparte dalla sua primaria.
	useEffect(() => {
		setActiveSport(null);
	}, [athleteId]);

	// Le etichette sono dato admin: si leggono dal pool, e solo se i tab ci sono.
	useEffect(() => {
		if (sportsOptions || !multi) return undefined;
		let cancelled = false;
		fetchAPI("/users/sports/list")
			.then((data) => {
				if (!cancelled) setLoadedLabels(Array.isArray(data) ? data : []);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [multi, sportsOptions]);

	// Con un solo sport niente `?sport=`: il server usa la primaria e la
	// chiamata resta identica a prima.
	const sportQuery =
		multi && currentSport ? `?sport=${encodeURIComponent(currentSport)}` : "";

	const load = useCallback(async () => {
		if (!athleteId) return;
		// Due tab cliccati in fretta: vince l'ultima richiesta, non la piu' lenta.
		const seq = ++requestSeq.current;
		setLoading(true);
		try {
			const data = await fetchAPI(
				`/coach/athletes/${athleteId}/performance-profile${sportQuery}`,
			);
			if (seq === requestSeq.current) setDomains(Array.isArray(data) ? data : []);
		} catch {
			if (seq === requestSeq.current) {
				setDomains([]);
				toast.error(tRef.current("profileLoadError"));
			}
		} finally {
			if (seq === requestSeq.current) setLoading(false);
		}
	}, [athleteId, sportQuery]);

	useEffect(() => {
		load();
	}, [load]);

	// Un altro atleta o un altro sport chiudono il confronto.
	useEffect(() => {
		setCompare(null);
	}, [athleteId, currentSport]);

	const compareKey = compare?.key || null;
	useEffect(() => {
		if (!athleteId || !compareKey) {
			setCompareDomains(null);
			return undefined;
		}
		let cancelled = false;
		setCompareDomains(null);
		const params = new URLSearchParams();
		if (currentSport) params.set("sport", currentSport);
		params.set("weight_class", compareKey);
		fetchAPI(`/coach/athletes/${athleteId}/performance-profile?${params.toString()}`)
			.then((data) => {
				if (!cancelled) setCompareDomains(Array.isArray(data) ? data : []);
			})
			.catch(() => {
				if (cancelled) return;
				setCompare(null);
				toast.error(tRef.current("profileLoadError"));
			});
		return () => {
			cancelled = true;
		};
	}, [athleteId, currentSport, compareKey]);

	const labels = sportsOptions || loadedLabels;

	return (
		<div className="space-y-3 w-full min-w-0">
			<SportTabs
				sports={sports}
				active={currentSport}
				onChange={setActiveSport}
				labelFor={(value) => formatSportLabel(value, labels)}
			/>
			{/* Con quale modello e' valutato in questo sport, e la scelta a mano
			    (docs/sport-config, D13): i livelli sotto dipendono da questo. */}
			{currentSport && (
				<AthleteModelBar
					athleteId={athleteId}
					sport={currentSport}
					onChanged={() => {
						// Un altro modello, altre tabelle: il confronto va rifatto.
						setCompare(null);
						load();
					}}
					compareKey={compareKey}
					onCompare={setCompare}
				/>
			)}
			{compare && (
				<WeightClassComparison
					base={domains}
					other={compareDomains}
					baseLabel={compare.baseLabel}
					otherLabel={compare.label}
					loading={!compareDomains}
					onClose={() => setCompare(null)}
				/>
			)}
			<PerformanceProfilePreview
				domains={domains}
				loading={loading}
				athleteId={athleteId}
				athleteSports={sports}
				sport={multi ? currentSport : null}
				onHistoryChanged={load}
			/>
		</div>
	);
}
